<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faTrashAlt, faSave } from '@fortawesome/free-solid-svg-icons'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import type { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import { Entry } from '../../../modules/financialApi/entries/entry'
    import type { CreateEntryDto } from '../../../modules/financialApi/entries/dto/create-entry.dto'
    import type { UpdateEntryDto } from '../../../modules/financialApi/entries/dto/update-entry.dto'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    const dispatch = createEventDispatcher()

    export let entryId: string = null
    export let creditDebit: EntryTypeEnum
    export let bank: string = null
    export let cashierId: string = null

    let show = false
    let entity: Entry = new Entry()
    // o Zinput de data e o de mês trabalham com Date (já na meia-noite local), então as datas não
    // circulam mais como texto 'YYYY-MM-DD' / 'YYYY-MM'
    let paidAt: Date = todayAtMidnight()
    let headline = ''

    // competência: no mensal guardamos a data do 1º dia do mês escolhido; no anual, só o ano
    let accrualMonthValue: Date = null
    let accrualYearValue: number = null
    let accrualPeriod: 'monthly' | 'yearly' = 'monthly'

    const accrualPeriodOptions = [
        { value: 'monthly', label: 'Mensal' },
        { value: 'yearly', label: 'Anual' },
    ]

    function todayAtMidnight() {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }

    // limpar um Zinput de data devolve um Invalid Date, que é truthy: testar só a existência deixa
    // passar campo vazio
    function isValidDate(d: any) {
        return d instanceof Date && !isNaN(d.getTime())
    }

    onMount(async () => {
        if (entryId) {
            await loadEntity(entryId)
        } else {
            newEntity()
        }
    })

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new Entry()
            entity = await window.api.financials.entries.getOne(id)
            paidAt = new Date(entity.paymentAt)
            entity.person = await window.api.events.persons.getOne(entity.personId)
            if (entity.accrualMonth) {
                accrualPeriod = 'monthly'
                accrualMonthValue = new Date(entity.accrualYear, entity.accrualMonth - 1, 1)
            } else {
                accrualPeriod = 'yearly'
                accrualYearValue = entity.accrualYear
            }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        if (!creditDebit) {
            throw new Error('Tipo (credito/debito) não informado')
        }
        entity = new Entry()
        entity.cashierId = cashierId || undefined
        entity.paymentAt = paidAt
        entity.type = creditDebit
    }

    async function saveEntity(close: boolean) {
        if (window.configs.financialShowAccrual) {
            if (accrualPeriod === 'monthly') {
                if (isValidDate(accrualMonthValue)) {
                    entity.accrualYear = accrualMonthValue.getFullYear()
                    entity.accrualMonth = accrualMonthValue.getMonth() + 1
                }
            } else if (accrualYearValue) {
                entity.accrualYear = accrualYearValue
                entity.accrualMonth = null
            }
        }
        if (entity.entryId) {
            updateEntity(close)
        } else {
            createEntity(close)
        }
    }

    async function createEntity(close: boolean) {
        try {
            if (show) return
            show = true
            if (!isValidDate(paidAt)) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.paymentAt = paidAt
            const dto: Partial<CreateEntryDto> = {
                type: entity.type,
                paymentAt: entity.paymentAt,
                personId: entity.person?.personId,
                description: entity.description,
                source: 'manual',
                value: entity.value,
                accountId: entity.account?.accountId,
                categoryId: entity.category?.categoryId,
                cashierId: entity.cashierId,
                accrualYear: entity.accrualYear,
                accrualMonth: entity.accrualMonth,
            }

            if (!validateDto(dto)) return false
            await window.api.financials.entries.create(dto)
            if (close) {
                dispatch('change')
                return
            }
            window.alert('Salvo com sucesso.')
            newEntity()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity(close: boolean) {
        try {
            if (show) return
            show = true
            if (!isValidDate(paidAt)) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.paymentAt = paidAt
            const dto: Partial<UpdateEntryDto> = {
                paymentAt: entity.paymentAt,
                personId: entity.person?.personId,
                description: entity.description,
                value: entity.value,
                accountId: entity.account?.accountId,
                categoryId: entity.category?.categoryId,
                accrualYear: entity.accrualYear,
                accrualMonth: entity.accrualMonth,
            }

            if (!validateDto(dto, false)) return false
            await window.api.financials.entries.update(entity.entryId, dto)
            if (close) {
                dispatch('change')
                return
            }
            window.alert('Salvo com sucesso.')
            newEntity()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto, isCreate = true) {
        if (!dto.description) {
            evaluateError('Informe a descrição')
            return false
        }
        if (!dto.value) {
            evaluateError('Informe o valor')
            return false
        }
        if (!dto.accountId) {
            evaluateError('Informe a conta')
            return false
        }
        if (!dto.categoryId) {
            evaluateError('Informe a categoria')
            return false
        }
        if (!dto.personId && isCreate) {
            if (!confirm('É recomendado informar a pessoa / empresa.\nDeseja mesmo continuar?')) return false
        }
        if (window.configs.financialShowAccrual) {
            if (!dto.accrualYear) {
                evaluateError('Informe o ano de competência')
                return false
            }
            if (dto.accrualMonth === '' || dto.accrualMonth === undefined) {
                evaluateError('Informe o mês de competência')
                return false
            }
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.financials.entries.remove(entity.entryId)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }
</script>

<div class="root">
    <Zact>
        {#if window.store.token.permissions.financial.entries.write}
            <div class="spacer"></div>
            {#if !!entity.entryId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" on:click={removeEntity} />
            {/if}
            {#if !entryId}
                <Zbutton color="secondary" icon={faSave} label="Salvar" disabled={show} on:click={() => saveEntity(false)} />
            {/if}
            <Zbutton color="primary" icon={faSave} label="Salvar e fechar" disabled={show} on:click={() => saveEntity(true)} />
        {/if}
    </Zact>
    <Zframe title="Dados do lançamento" {headline} class="m-3">
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput size={12} label="Descrição" type="text" bind:value={entity.description} />
            {#if window.configs.financialShowAccrual}
                <Zinput size={4} sizeM={12} label="Tipo de competência" type="select" options={accrualPeriodOptions} bind:value={accrualPeriod} />
                {#if accrualPeriod === 'monthly'}
                    <Zinput size={8} sizeM={12} label="Mês de competência" type="month" bind:value={accrualMonthValue} />
                {:else}
                    <Zinput size={8} sizeM={12} label="Ano de competência" type="number" bind:value={accrualYearValue} />
                {/if}
            {/if}
            <Zinput size={4} sizeM={12} label="Vencimento" type="date" bind:value={paidAt} />
            <Zinput size={4} sizeM={12} label="Valor" type="number" bind:value={entity.value} />
            <ZContainerCol size={4} sizeM={12}>
                <ZentitySelect fonte="pessoa" label="Pessoa / Empresa" bind:value={entity.person} />
            </ZContainerCol>
        </ZContainer>
    </Zframe>

    <Zframe title="Classificação" class="m-3 mt-0">
        <ZContainer gapRow="8px" gapCol="8px">
            <ZContainerCol size={6} sizeM={12}>
                <ZentitySelect fonte="conta" label="Conta" bind:value={entity.account} params={{ bank }} />
            </ZContainerCol>
            <ZContainerCol size={6} sizeM={12}>
                <ZentitySelect fonte="categoriaFinanceira" label="Categoria" bind:value={entity.category} params={{ creditDebit }} />
            </ZContainerCol>
        </ZContainer>
    </Zframe>
    <LoadSpinner {show} />
</div>
